'use client'

import { useCallback, useEffect, useState, useRef } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import SectionHeader from '@/components/ui/SectionHeader'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { track } from '@/utilities/analytics'

interface Step {
  id?: string | null
  title: string
  description: string
  number: number
}

interface HeaderGroup {
  infoTagText?: string | null
  title?: string | null
  subtitle?: string | null
}

interface CarouselSettings {
  enableAutoAdvance?: boolean | null
  autoAdvanceInterval?: number | null
}

interface ProcessCarouselClientProps {
  header?: HeaderGroup | null
  steps: Step[]
  carouselSettings?: CarouselSettings | null
}

export default function ProcessCarouselClient({
  header,
  steps,
  carouselSettings,
}: ProcessCarouselClientProps) {
  const [emblaRef, embla] = useEmblaCarousel({ align: 'start', loop: false })
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const onSelect = useCallback(() => {
    if (!embla) return
    setCanLeft(embla.canScrollPrev())
    setCanRight(embla.canScrollNext())
    const index = embla.selectedScrollSnap()
    const step = steps[index]
    if (step) {
      track('process_step_viewed', {
        step_index: index,
        step_label: step.title,
      })
    }
  }, [embla, steps])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on('select', onSelect)
    embla.on('reInit', onSelect)
    return () => {
      embla.off('select', onSelect)
      embla.off('reInit', onSelect)
    }
  }, [embla, onSelect])

  // Auto-advance logic
  useEffect(() => {
    if (!embla || !carouselSettings?.enableAutoAdvance) return

    const interval = setInterval(() => {
      if (embla.canScrollNext()) {
        embla.scrollNext()
      } else {
        // Loop back to start
        embla.scrollTo(0)
      }
    }, carouselSettings.autoAdvanceInterval || 5000)

    return () => clearInterval(interval)
  }, [embla, carouselSettings])

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla])
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla])

  return (
    <div>
      {header && (header.infoTagText || header.title || header.subtitle) && (
        <SectionHeader
          infoTagText={header.infoTagText || ''}
          title={header.title || ''}
          size="md"
          subtitle={header.subtitle || undefined}
          align="left"
        />
      )}

      <div className="space-y-6">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6 items-stretch">
            {steps.map((step, index) => (
              <div
                key={step.id || index}
                className="flex-none w-[240px] md:w-[350px] lg:w-[500px] flex"
              >
                <div className="bg-brand-surface-1 px-8 py-10 flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-8 h-full w-full">
                  <div className="display-lg text-brand leading-none numeric-tight select-none whitespace-nowrap flex-shrink-0">
                    {String(step.number).padStart(2, '0')}
                  </div>
                  <div className="max-w-prose text-center md:text-left">
                    <h3 className="title-md text-primary uppercase">{step.title}</h3>
                    <p className="mt-2 body-base text-secondary">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={scrollPrev}
            disabled={!canLeft}
            className="inline-flex items-center gap-2 bg-brand text-text-white px-6 py-3 font-metropolis disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>ZURÜCK</span>
          </button>
          <button
            type="button"
            onClick={scrollNext}
            disabled={!canRight}
            className="inline-flex items-center gap-2 bg-brand text-text-white px-6 py-3 font-metropolis disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span>WEITER</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
